import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  isPrivateStorageAssetUrl,
  loadAssetPreview,
  shouldImmediatelyProxyAsset,
} from '../services/assetPreviewService';

const AuthenticatedAssetImage = ({
  src,
  fallbackSrc,
  assetSize = 'full',
  onError,
  ...imageProps
}) => {
  const [resolvedSrc, setResolvedSrc] = useState(
    (assetSize !== 'full' && isPrivateStorageAssetUrl(src))
      || shouldImmediatelyProxyAsset(src)
      ? ''
      : src
  );
  const objectUrlRef = useRef(null);
  const requestIdRef = useRef(0);

  const revokeObjectUrl = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  const loadThroughBackend = useCallback(async (sourceUrl) => {
    if (!isPrivateStorageAssetUrl(sourceUrl)) return false;

    const requestId = ++requestIdRef.current;
    try {
      const blob = await loadAssetPreview(sourceUrl, assetSize);
      if (requestId !== requestIdRef.current) return false;
      revokeObjectUrl();
      objectUrlRef.current = URL.createObjectURL(blob);
      setResolvedSrc(objectUrlRef.current);
      return true;
    } catch {
      if (requestId === requestIdRef.current) setResolvedSrc(sourceUrl);
      return false;
    }
  }, [assetSize, revokeObjectUrl]);

  useEffect(() => {
    let active = true;
    requestIdRef.current += 1;
    revokeObjectUrl();

    if (assetSize !== 'full' && isPrivateStorageAssetUrl(src)) {
      loadThroughBackend(src).then((loaded) => {
        if (active && !loaded) setResolvedSrc(src);
      });
    } else if (shouldImmediatelyProxyAsset(src)) {
      loadThroughBackend(src).then((loaded) => {
        if (active && !loaded) setResolvedSrc(src);
      });
    } else {
      setResolvedSrc(src);
    }

    return () => {
      active = false;
      requestIdRef.current += 1;
      revokeObjectUrl();
    };
  }, [src, loadThroughBackend, revokeObjectUrl]);

  const handleError = async (event) => {
    if (fallbackSrc && resolvedSrc !== fallbackSrc) {
      setResolvedSrc(fallbackSrc);
      return;
    }

    const loaded = await loadThroughBackend(fallbackSrc || src);
    if (!loaded && onError) onError(event);
  };

  return (
    <img
      {...imageProps}
      src={resolvedSrc || undefined}
      onError={handleError}
    />
  );
};

export default AuthenticatedAssetImage;
